import { apiFetch, tokenStore } from "./client";

export type AccountStatus = "active" | "suspended" | "deleted" | "pending_validation";
export type AccountSource = "agent" | "manual";
export type AccountSyncStatus = "never" | "ok" | "stale" | "error" | "checkpoint" | "banned";
export type MediaType = "photo" | "video" | "carousel" | "reel";

export interface AccountListRow {
  id: string;
  ig_username: string;
  full_name: string | null;
  email: string | null;
  phone_number: string | null;
  two_fa_method: "totp" | "sms" | "none";
  status: AccountStatus;
  source: AccountSource;
  sync_status: AccountSyncStatus;
  proxy_used: string | null;
  profile_pic_path: string | null;
  followers: number | null;
  following: number | null;
  posts_count: number | null;
  last_synced_at: string | null;
  created_at: string;
  phone_id: string | null;
  phone_name: string | null;
}

export interface AccountDetail extends AccountListRow {
  ig_user_id: string | null;
  biography: string | null;
  is_private: boolean | null;
  password: string | null;
  two_fa_secret: string | null;
  notes: string | null;
  signup_metadata: Record<string, unknown>;
  run_id: string | null;
  created_by: string | null;
  media_count: number;
  last_post_at: string | null;
  total_likes: number;
  total_views: number;
  warmup_minutes: number;
  updated_at: string;
}

export interface WarmupSession {
  id: string;
  started_at: string;
  duration_minutes: number;
  note: string | null;
  created_at: string;
}

export interface CreateWarmupInput {
  started_at: string;
  duration_minutes: number;
  note?: string;
}

export interface AccountMetricPoint {
  followers: number | null;
  following: number | null;
  posts_count: number | null;
  fetched_at: string;
}

export interface AccountMedia {
  id: string;
  ig_media_id: string;
  media_type: MediaType;
  caption: string | null;
  thumbnail_path: string | null;
  permalink: string | null;
  taken_at: string | null;
  like_count: number | null;
  comment_count: number | null;
  view_count: number | null;
  last_synced_at: string;
}

export interface AccountStats {
  total: number;
  active: number;
  suspended: number;
  deleted: number;
  pending_validation: number;
  posts_today: number;
}

export interface AccountListResponse {
  success: boolean;
  accounts: AccountListRow[];
  pagination: { total: number; limit: number; offset: number };
  stats: AccountStats;
}

export interface AccountFilters {
  status?: AccountStatus | "";
  phone_id?: string;
  source?: AccountSource | "";
  sync_status?: AccountSyncStatus | "";
  q?: string;
  date_from?: string;
  date_to?: string;
  sort?: "created_at" | "followers" | "posts_count" | "last_synced_at";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface CreateAccountInput {
  ig_username: string;
  ig_user_id?: string;
  full_name?: string;
  email?: string;
  phone_number?: string;
  password?: string;
  two_fa_secret?: string;
  two_fa_method?: "totp" | "sms" | "none";
  status?: AccountStatus;
  phone_id?: string;
  proxy_used?: string;
  notes?: string;
}

export type AccountPatch = Partial<{
  status: AccountStatus;
  full_name: string | null;
  email: string | null;
  phone_number: string | null;
  password: string | null;
  two_fa_secret: string | null;
  two_fa_method: "totp" | "sms" | "none";
  notes: string | null;
  phone_id: string | null;
}>;

function buildQuery(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "" && v !== null) {
      params.set(k, String(v));
    }
  }
  const q = params.toString();
  return q ? `?${q}` : "";
}

export async function listAccounts(filters: AccountFilters = {}): Promise<AccountListResponse> {
  return apiFetch<AccountListResponse>(`/api/accounts${buildQuery(filters as Record<string, unknown>)}`);
}

export async function getAccount(id: string): Promise<AccountDetail> {
  const data = await apiFetch<{ success: boolean; account: AccountDetail }>(
    `/api/accounts/${id}`
  );
  // Les SUM PG reviennent en bigint -> string : on normalise en number.
  const a = data.account;
  return {
    ...a,
    total_likes: Number(a.total_likes ?? 0),
    total_views: Number(a.total_views ?? 0),
    warmup_minutes: Number(a.warmup_minutes ?? 0),
  };
}

export async function createAccount(input: CreateAccountInput): Promise<AccountDetail> {
  const data = await apiFetch<{ success: boolean; account: AccountDetail }>(`/api/accounts`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.account;
}

export async function updateAccount(id: string, patch: AccountPatch): Promise<void> {
  await apiFetch(`/api/accounts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function updateAccountStatus(id: string, status: AccountStatus): Promise<void> {
  await updateAccount(id, { status });
}

export async function deleteAccount(id: string): Promise<void> {
  await apiFetch(`/api/accounts/${id}`, { method: "DELETE" });
}

export async function getAccountMetrics(
  id: string,
  range: "7d" | "30d" | "90d" | "all" = "30d"
): Promise<AccountMetricPoint[]> {
  const data = await apiFetch<{ success: boolean; metrics: AccountMetricPoint[] }>(
    `/api/accounts/${id}/metrics?range=${range}`
  );
  return data.metrics;
}

export async function getAccountMedia(
  id: string,
  opts: { limit?: number; offset?: number } = {}
): Promise<{ media: AccountMedia[]; pagination: { total: number; limit: number; offset: number } }> {
  return apiFetch(`/api/accounts/${id}/media${buildQuery(opts)}`);
}

export async function syncAccount(id: string): Promise<void> {
  await apiFetch(`/api/accounts/${id}/sync`, { method: "POST" });
}

export async function listWarmup(id: string): Promise<WarmupSession[]> {
  const data = await apiFetch<{ success: boolean; warmup: WarmupSession[] }>(
    `/api/accounts/${id}/warmup`
  );
  return data.warmup;
}

export async function createWarmup(id: string, input: CreateWarmupInput): Promise<WarmupSession> {
  const data = await apiFetch<{ success: boolean; warmup: WarmupSession }>(
    `/api/accounts/${id}/warmup`,
    { method: "POST", body: JSON.stringify(input) }
  );
  return data.warmup;
}

export async function deleteWarmup(id: string, warmupId: string): Promise<void> {
  await apiFetch(`/api/accounts/${id}/warmup/${warmupId}`, { method: "DELETE" });
}

// Images (avatar / thumbnail) : servies en JPEG derrière le JWT. Comme un <img>
// ne peut pas porter le header Authorization, on fetch en blob et on crée une
// Object URL (même pattern que fetchFailureScreenshot). Le caller doit
// révoquer l'URL via URL.revokeObjectURL quand il n'en a plus besoin.
async function fetchImageObjectUrl(path: string): Promise<string | null> {
  const token = tokenStore.get();
  const res = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ""}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function fetchAccountAvatar(id: string): Promise<string | null> {
  return fetchImageObjectUrl(`/api/accounts/${id}/avatar`);
}

export function fetchMediaThumb(mediaId: string): Promise<string | null> {
  return fetchImageObjectUrl(`/api/accounts/media/${mediaId}/thumb`);
}

// Téléchargement TXT : on doit gérer le Bearer token manuellement (pas via apiFetch)
// car la réponse est text/plain, pas JSON.
export async function downloadExportTxt(filters: AccountFilters = {}): Promise<void> {
  const token = tokenStore.get();
  const res = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ""}/api/accounts/export.txt${buildQuery(filters as Record<string, unknown>)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Export échoué (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `velvet-accounts-${new Date().toISOString().slice(0, 10)}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
